import React, { useEffect, useMemo, useState } from "react";
import { Label, Select } from "@src/common/components";
import {
  Controller,
  FieldErrors,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { useCreateOrderContext } from "@src/orders/create/utils/CreateOrderContextProvider";
import {
  CostCenter,
  DispatcherOrderEntryAdditionalField,
  useGetCostCentersLazyQuery,
} from "@api/graphql/generated/generated-types";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { useAuth } from "@src/common/hooks/useAuth";
import { FormErrorMessage } from "@src/common/components";

interface Props {
  formErrors: FieldErrors<FieldValues>;
}

const CostCenterSelector: React.FC<Props> = ({ formErrors }: Props) => {
  const [retrievedCostCenters, setRetrievedCostCenters] = useState<
    CostCenter[]
  >([]);
  const [fetchCostCenters, { loading }] = useGetCostCentersLazyQuery({
    fetchPolicy: "no-cache",
  });
  const { control, watch, setValue } = useFormContext();
  const {
    selectedCustomer,
    setChosenCostCenter,
    setIsCostCenterModalOpen,
    chosenCostCenter,
  } = useCreateOrderContext();
  const { courierId, courier } = useAuth();
  const { showToast } = useCourieStore();

  const endCustomerId = watch("endCustomerId");

  const costCenterRequired = useMemo(() => {
    if (selectedCustomer?.dispatcherOrderEntryAdditionalFields) {
      return selectedCustomer.dispatcherOrderEntryAdditionalFields.includes(
        DispatcherOrderEntryAdditionalField.CostCenter
      );
    }
    if (courier?.courier?.dispatcherOrderEntryAdditionalFields) {
      return courier.courier.dispatcherOrderEntryAdditionalFields.includes(
        DispatcherOrderEntryAdditionalField.CostCenter
      );
    }
    return false;
  }, [selectedCustomer, courier]);

  useEffect(() => {
    if (selectedCustomer) {
      setChosenCostCenter(undefined);
      setValue("costCenterId", "");
      handleCostCenterChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  useEffect(() => {
    if (chosenCostCenter) {
      setValue("costCenterId", [chosenCostCenter]);
      handleCostCenterChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenCostCenter]);

  const handleCostCenterChange = () => {
    if (selectedCustomer && courierId) {
      fetchCostCenters({
        variables: {
          courierId: courierId,
          endCustomerIds: selectedCustomer.id,
        },
      })
        .then((response) => {
          const costCenters = response.data?.costCenters || [];
          setRetrievedCostCenters(costCenters);
        })
        .catch((error) => {
          showErrorToast(error, showToast);
        });
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <Label
          htmlFor="costCenterId"
          className="text-xs font-medium text-gray-700 dark:text-gray-300 flex justify-between"
        >
          <div className="truncate">
            Cost Center{" "}
            {costCenterRequired ? (
              <span className="text-orange-400 dark:text-orange-500 text-xs">(Required)</span>
            ) : (
              <span className="text-slate-400 dark:text-gray-500 text-xs">(Optional)</span>
            )}
          </div>
          {endCustomerId.length > 0 && (
            <span
              className="text-xs hover:underline hover:cursor-pointer text-blue-500 dark:text-blue-400 truncate"
              onClick={() => setIsCostCenterModalOpen(true)}
            >
              Add New Cost Center
            </span>
          )}
        </Label>
        <Controller
          control={control}
          name="costCenterId"
          rules={
            costCenterRequired ? { required: "Cost Center is required" } : {}
          }
          render={({ field: { value, onChange } }) => (
            <Select
              size="mini"
              isLoading={loading}
              options={retrievedCostCenters}
              labelKey="name"
              valueKey="id"
              onChange={({ value }) => {
                const selectedCostCenter: CostCenter | undefined =
                  value[0] as CostCenter;
                setChosenCostCenter(selectedCostCenter);
                onChange(value);
              }}
              value={value}
              error={!!formErrors["costCenterId"]}
              disabled={endCustomerId.length === 0}
            />
          )}
        />
        <span className="text-red-500 dark:text-red-400 text-xs">
          <FormErrorMessage errors={formErrors} name="costCenterId" />
        </span>
      </div>
    </>
  );
};

export default CostCenterSelector;
